import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Ban, RotateCcw, History, Loader2, Building2, Mail, Phone, User } from "lucide-react";
import { SocietyStateBadge, useForm } from "@/components/developer/shared";

const FIELDS = [
  ["company_name", "Company name"], ["contact_name", "Contact person name"], ["email", "Email"],
  ["phone", "Phone"], ["username", "Username"],
];
const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", username: "", password: "" };

function DeveloperDialog({ dev, onClose, onSaved }) {
  const { form, bind } = useForm(dev ? { ...EMPTY, ...dev, password: "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (dev && !body.password) delete body.password;
      if (dev) await api.put(`/super/developers/${dev.id}`, body);
      else await api.post("/super/developers", body);
      toast.success(dev ? "Developer updated" : "Developer added");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="developer-dialog">
        <DialogHeader><DialogTitle>{dev ? "Edit developer" : "Add developer"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "company_name" ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{label}</Label>
              <Input className="mt-1" data-testid={`developer-${k.replace("_", "-")}-input`} {...bind(k)} />
            </div>
          ))}
          <div>
            <Label className="text-xs">{dev ? "New password (optional)" : "Password"}</Label>
            <Input className="mt-1" type="text" data-testid="developer-password-input" placeholder={dev ? "leave blank to keep" : "min 6 characters"} {...bind("password")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="developer-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="developer-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityDialog({ dev, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/super/developers/${dev.id}/activity`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [dev.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="developer-activity-dialog">
        <DialogHeader><DialogTitle>Activity · {dev.company_name}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {rows === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" />
            : rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="developer-activity-empty">No activity yet.</p>
            : rows.map((a) => (
              <div key={a.id} className="rounded-md border border-border px-3 py-2" data-testid={`developer-activity-${a.id}`}>
                <p className="text-sm text-zinc-800">{a.summary}</p>
                <p className="text-[11px] text-zinc-400">{new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeveloperCard({ d, onEdit, onActivity, onToggle }) {
  const suspended = d.status === "suspended";
  return (
    <div className={`rounded-xl border border-border bg-card p-5 ${suspended ? "opacity-70" : ""}`} data-testid={`developer-card-${d.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-cabinet text-lg font-bold text-brand">{d.company_name}</h3>
            <Badge variant="outline" data-testid={`developer-status-${d.id}`} className={suspended ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}>{suspended ? "Suspended" : "Active"}</Badge>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.contact_name} (@{d.username})</span>
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{d.email}</span>
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{d.phone}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`developer-edit-${d.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
          <Button size="sm" variant="outline" onClick={onActivity} data-testid={`developer-activity-btn-${d.id}`}><History className="mr-1 h-3.5 w-3.5" />Activity</Button>
          <Button size="sm" variant={suspended ? "default" : "destructive"} onClick={onToggle} data-testid={`developer-toggle-${d.id}`}>
            {suspended ? <><RotateCcw className="mr-1 h-3.5 w-3.5" />Reactivate</> : <><Ban className="mr-1 h-3.5 w-3.5" />Suspend</>}
          </Button>
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Societies ({d.societies.length})</p>
        {d.societies.length === 0 && <p className="text-xs text-zinc-400">No societies yet.</p>}
        {d.societies.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary/60 px-3 py-2" data-testid={`developer-society-${s.id}`}>
            <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-800"><Building2 className="h-3.5 w-3.5 text-brand-green" />{s.name} <span className="text-xs text-zinc-400">{s.code}</span></span>
            <SocietyStateBadge s={s} testid={`developer-society-state-${s.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Developers() {
  const [devs, setDevs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [activity, setActivity] = useState(null);
  const load = () => api.get("/super/developers").then((r) => setDevs(r.data)).catch(() => setDevs([]));
  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    const status = d.status === "suspended" ? "active" : "suspended";
    try {
      await api.post(`/super/developers/${d.id}/status`, { status });
      toast.success(status === "suspended" ? "Developer suspended — their societies keep working" : "Developer reactivated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Developers" subtitle="Partners" description="Developer accounts, their societies and handover status.">
        <Button onClick={() => setEditing("new")} data-testid="add-developer-btn"><Plus className="mr-1.5 h-4 w-4" />Add Developer</Button>
      </PageHeader>
      {devs === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-zinc-400" />
        : devs.length === 0 ? <p className="text-sm text-zinc-500" data-testid="developers-empty">No developers yet.</p>
        : <div className="space-y-4">{devs.map((d) => (
          <DeveloperCard key={d.id} d={d} onEdit={() => setEditing(d)} onActivity={() => setActivity(d)} onToggle={() => toggle(d)} />
        ))}</div>}
      {editing && <DeveloperDialog dev={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {activity && <ActivityDialog dev={activity} onClose={() => setActivity(null)} />}
    </div>
  );
}
